import {
  EMPTY_MERGED_LOG_LINES,
  type LogLine,
  type MergedLogLines,
  mergeLogLines,
} from "@store/api/utils";
import { useEffect, useState } from "react";

import { getJobLogs } from "../api";

const STREAM_QUIET_DELAY_MS = 1000;

type Options = {
  jobId: string;
  runId?: string;
  enabled: boolean;
};

export function useJobLogs({ jobId, runId, enabled }: Options) {
  const [logsState, setLogsState] = useState<MergedLogLines>(
    EMPTY_MERGED_LOG_LINES,
  );
  const [awaitingLogs, setAwaitingLogs] = useState(true);
  const [logsError, setLogsError] = useState(false);

  useEffect(() => {
    let active = true;
    let timeout: ReturnType<typeof setTimeout>;
    let reader: ReadableStreamDefaultReader<LogLine[]> | undefined;

    setLogsState(EMPTY_MERGED_LOG_LINES);
    setAwaitingLogs(true);
    setLogsError(false);

    if (!enabled || !runId) {
      return () => {
        active = false;
      };
    }

    const resetQuietTimeout = () => {
      clearTimeout(timeout);
      // Set awaitingLogs to false once the websocket hasn't returned anything
      // in the last second... at first it will just be fetching past logs.
      timeout = setTimeout(() => {
        if (active) {
          setAwaitingLogs(false);
        }
      }, STREAM_QUIET_DELAY_MS);
    };

    const readLogs = async () => {
      try {
        const source = await getJobLogs({ jobId, runId });
        if (!active) {
          if (source instanceof ReadableStream) {
            await source.cancel();
          }
          return;
        }
        if (!(source instanceof ReadableStream)) {
          setLogsState(mergeLogLines(EMPTY_MERGED_LOG_LINES, source));
          setAwaitingLogs(false);
          return;
        }

        reader = source.getReader();
        resetQuietTimeout();
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (!active) {
            break;
          }
          if (done) {
            setAwaitingLogs(false);
            // we don't have anything else to do on a successful close for now
            // since this should normally correspond with a state change from
            // RUNNING to some non-alive state
            break;
          }
          if (value?.length) {
            setLogsState((previous) => mergeLogLines(previous, value));
          }
          resetQuietTimeout();
        }
      } catch (error) {
        // A rejection during teardown (run change / unmount cancels the
        // stream) isn't a real error, so don't surface the banner for it.
        if (active) {
          console.error("Error reading job logs:", error);
          setLogsError(true);
          setAwaitingLogs(false);
        }
      } finally {
        reader?.releaseLock();
        reader = undefined;
      }
    };

    void readLogs();

    return () => {
      active = false;
      clearTimeout(timeout);
      void reader?.cancel().catch(() => undefined);
    };
  }, [enabled, jobId, runId]);

  return { logs: logsState.lines, awaitingLogs, logsError };
}
